import React, { useState } from "react";
import SectionHeader from "./SectionHeader";

const KPIS = [
  { v: "~70%", t: "less time on case-law research per matter" },
  { v: "3×", t: "faster from notice to first cited draft" },
  { v: "17 days", t: "earlier reply against a 30-day notice window" },
  { v: "6×", t: "more time freed for strategy and clients" },
];

const TASKS = [
  { t: "Reply to a GST show-cause notice", manual: 16, vedum: 5 },
  { t: "Case-law research memo", manual: 12, vedum: 3 },
  { t: "Hearing preparation & compilation", manual: 10, vedum: 4 },
  { t: "Cause-list & hearing tracking (weekly)", manual: 5, vedum: 0.5 },
  { t: "Citation verification", manual: 4, vedum: 0.5 },
];

// Days per phase out of the 30-day window. Adjust to your real benchmarks.
const PHASES = [
  { name: "Intake", cls: "vd-c-peri" },
  { name: "Research", cls: "vd-c-navy" },
  { name: "Drafting", cls: "vd-c-amber" },
  { name: "Review", cls: "vd-c-rust" },
  { name: "Filing", cls: "vd-c-ink" },
];
const RESPONSE = [
  { name: "Manual workflow", days: [2, 8, 8, 4, 4] },
  { name: "With Vedum", days: [1, 2, 3, 2, 1] },
];

const WEEK = [
  { name: "Research", cls: "vd-c-navy" },
  { name: "Drafting", cls: "vd-c-peri" },
  { name: "Tracking & admin", cls: "vd-c-grey" },
  { name: "Strategy, hearings & clients", cls: "vd-c-amber" },
];
const WEEK_ROWS = [
  { name: "Before Vedum", v: [45, 30, 15, 10] },
  { name: "With Vedum", v: [20, 15, 5, 60] },
];

const HOURS_PER_NOTICE = 11;
const fmt = (n) => n.toLocaleString("en-IN");

const Impact = () => {
  const [matters, setMatters] = useState(20);
  const hm = matters * HOURS_PER_NOTICE;

  return (
    <section id="impact" className="vd-section">
      <div className="container">
        <SectionHeader
          eyebrow="THE IMPACT"
          title="Faster responses to courts and authorities. More time for the work that wins."
        >
          Automating research, citation checks, drafting and hearing tracking
          compresses the time between receiving a notice and filing a considered
          reply.
        </SectionHeader>

        <div className="row g-3 mb-4">
          {KPIS.map((k) => (
            <div className="col-6 col-lg-3" key={k.v}>
              <div className="vd-kpi">
                <b>{k.v}</b>
                <span>{k.t}</span>
              </div>
            </div>
          ))}
        </div>

        <div className="row g-4">
          {/* Hours per task */}
          <div className="col-lg-6">
            <figure className="vd-figure">
              <figcaption>
                <div className="vd-figure__title">Hours per task</div>
                <div className="vd-figure__sub">
                  Typical effort, manual workflow vs. with Vedum
                </div>
              </figcaption>
              <div className="vd-legend">
                <span>
                  <i className="vd-dot vd-c-grey" /> Manual
                </span>
                <span>
                  <i className="vd-dot vd-c-navy" /> With Vedum
                </span>
              </div>
              {TASKS.map((t) => (
                <div className="vd-hbar" key={t.t}>
                  <div className="vd-hbar__label">{t.t}</div>
                  <div className="vd-hbar__row">
                    <i
                      className="vd-c-grey"
                      style={{ width: `${(t.manual / 16) * 80}%` }}
                    />
                    <span>{t.manual} h</span>
                  </div>
                  <div className="vd-hbar__row">
                    <i
                      className="vd-c-navy"
                      style={{ width: `${(t.vedum / 16) * 80}%` }}
                    />
                    <span>{t.vedum} h</span>
                  </div>
                </div>
              ))}
            </figure>
          </div>

          {/* Response time */}
          <div className="col-lg-6">
            <figure className="vd-figure">
              <figcaption>
                <div className="vd-figure__title">Response time</div>
                <div className="vd-figure__sub">
                  Days from receiving a notice to filing the reply, against a
                  30-day window
                </div>
              </figcaption>
              <div className="vd-legend">
                {PHASES.map((p) => (
                  <span key={p.name}>
                    <i className={`vd-dot ${p.cls}`} /> {p.name}
                  </span>
                ))}
              </div>
              {RESPONSE.map((r) => (
                <div className="vd-forum" key={r.name}>
                  <div className="vd-forum__head">
                    <strong>{r.name}</strong>
                    <span>{r.days.reduce((a, b) => a + b, 0)} days</span>
                  </div>
                  <div
                    className="vd-stack vd-stack--sm"
                    style={{ width: "100%" }}
                  >
                    {r.days.map((d, i) => (
                      <span
                        key={i}
                        className={PHASES[i].cls}
                        style={{ width: `${(d / 30) * 100}%` }}
                      />
                    ))}
                  </div>
                </div>
              ))}
              <div className="vd-axis-row">
                <span>Day 0 · notice received</span>
                <span>Day 15</span>
                <span>Day 30 · deadline</span>
              </div>
              <div className="vd-figure__foot">
                Replying <strong>17 days earlier</strong> leaves room for senior
                review, client sign-off and a considered reply — instead of a
                last-day filing or a request for more time.
              </div>
            </figure>
          </div>

          {/* Junior's week */}
          <div className="col-12">
            <figure className="vd-figure">
              <figcaption className="d-flex flex-wrap justify-content-between gap-3 mb-3">
                <div>
                  <div className="vd-figure__title">
                    Where a junior’s week goes
                  </div>
                  <div className="vd-figure__sub mb-0">
                    Share of a typical working week on a litigation matter
                  </div>
                </div>
                <div className="vd-legend mb-0">
                  {WEEK.map((w) => (
                    <span key={w.name}>
                      <i className={`vd-dot ${w.cls}`} /> {w.name}
                    </span>
                  ))}
                </div>
              </figcaption>
              {WEEK_ROWS.map((r) => (
                <div className="mb-3" key={r.name}>
                  <div className="small fw-semibold mb-1">{r.name}</div>
                  <div className="vd-stack">
                    {r.v.map((p, i) => (
                      <span
                        key={i}
                        className={WEEK[i].cls}
                        style={{ width: `${p}%` }}
                      >
                        {p >= 10 ? `${p}%` : ""}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </figure>
          </div>
        </div>

        {/* Calculator */}
        <div className="vd-calc">
          <div className="row g-4 align-items-center">
            <div className="col-lg-5">
              <div className="vd-eyebrow mb-2" style={{ color: "#F2A33A" }}>
                ESTIMATE YOUR TIME SAVED
              </div>
              <label htmlFor="v-matters">
                Notices &amp; matters handled per month:{" "}
                <strong>{matters}</strong>
              </label>
              <input
                id="v-matters"
                type="range"
                min="5"
                max="200"
                step="5"
                value={matters}
                onChange={(e) => setMatters(Number(e.target.value))}
              />
              <div
                className="d-flex justify-content-between small"
                style={{ color: "#c9d4ea" }}
              >
                <span>5</span>
                <span>200</span>
              </div>
            </div>
            <div className="col-lg-7">
              <div className="row text-center g-3 vd-calc__out">
                <div className="col-4">
                  <b>{fmt(hm)}</b>
                  <span>hours saved / month</span>
                </div>
                <div className="col-4">
                  <b>{fmt(Math.round(hm / 8))}</b>
                  <span>working days / month</span>
                </div>
                <div className="col-4">
                  <b>{fmt(hm * 12)}</b>
                  <span>hours saved / year</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <p className="vd-small-note">
          Impact figures and calculator use an indicative model (≈11 hours saved
          per notice reply) based on typical indirect-tax and commercial
          litigation workflows. [Replace with Vedum customer benchmarks before
          publishing.] Actual results vary by matter, forum and team.
        </p>
      </div>
    </section>
  );
};

export default Impact;
